import { useGSAP } from '@gsap/react'
import { useInView } from 'framer-motion'
import { gsap } from 'gsap'
import { ReactNode, use, useEffect, useRef, useState } from 'react'

import useMediaQuery from './hooks/useMediaQuery'

const Card = ({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) => {
  const ref = useRef(null)
  const borderRef = useRef(null)
  const inView = useInView(ref, {
    once: false,
    amount: 0,
  })
  const isMobile = useMediaQuery('(max-width: 1024px)')
  const hoverTl = useRef(gsap.timeline({ paused: true }))
  const [gradientAnimationClass, setGradientAnimationClass] = useState(
    'before:[animation-play-state:running]',
  )
  const [glowAnimationClass, setGlowAnimationClass] = useState(
    'md:after:[animation-play-state:running] md:after:opacity-40',
  )

  const { contextSafe } = useGSAP(() => {}, { scope: ref })

  const handleAnimationClass = contextSafe(() => {
    const value = { current: 0 }
    gsap.timeline().to(value, {
      current: 1,
      duration: 2,
      ease: 'linear',
      onComplete: () => {
        setGradientAnimationClass('before:[animation-play-state:paused]')
        setGlowAnimationClass('md:after:[animation-play-state:paused] md:after:opacity-0')
      },
    })
  })

  useEffect(() => {
    if (inView) {
      handleAnimationClass()
      return
    }
    setGradientAnimationClass('before:[animation-play-state:running]')
    setGlowAnimationClass('md:after:[animation-play-state:running] md:after:opacity-40')
  }, [handleAnimationClass, inView])

  const onHover = contextSafe(() => {
    hoverTl.current.play()
  })

  const onHoverOut = contextSafe(() => {
    hoverTl.current.pause()
  })

  return (
    <div
      className="hit-box group/card relative perspective-1600 preserve-3d h-full w-full flex-shrink-0 flex-grow-0 z-10
      hover:z-50
    "
    >
      <div
        ref={ref}
        className={`relative h-full w-full rounded-card-bg md:rounded-card-bg-lg
        ${inView || isMobile ? 'opacity-100 duration-700 translate-y-0' : 'opacity-0 translate-y-28'}
            transition duration-1000 ease-expo-out transform group-hover/card:-rotate-x-6 group-hover/card:scale-95
        `}
        onMouseEnter={onHover}
        onMouseLeave={onHoverOut}
      >
        <div
          className={`flex flex-col col-span-1 items-start justify-start p-px md:p-[2px] rounded-card-bg md:rounded-card-bg-lg w-full relative ${className} z-0
          transition duration-1000 ease-expo-out relative

          before:absolute before:content-[''] before:inset-0 before:z-0 before:pointer-events-none
          before:card-border-bg before:rounded-card-bg before:md:rounded-card-bg-lg before:opacity-40
          before:group-hover/card:opacity-60 before:transition before:duration-1000 before:ease-linear
          ${gradientAnimationClass} before:group-hover/card:[animation-play-state:running]
          
          md:after:absolute md:after:content-[''] md:after:inset-0 md:after:z-0 md:after:pointer-events-none
          md:after:card-border-bg md:after:rounded-card-bg md:after:md:rounded-card-bg-lg md:after:blur-3xl
          md:after:group-hover/card:opacity-40 md:after:transition md:after:duration-1000 md:after:ease-linear
          ${glowAnimationClass} md:after:group-hover/card:[animation-play-state:running] 
        `}
        >
          <div
            className={`h-full w-full flex flex-col items-center justify-start rounded-card md:rounded-card-lg overflow-hidden relative bg-black z-10`}
          >
            <div
              className={`grid grid-pile relative w-full h-full transition ease-out-expo duration-1500 z-10 pointer-events-auto
                ${inView || isMobile ? 'opacity-100  translate-y-0 delay-500' : 'opacity-0 translate-y-4 delay-300'}
              `}
            >
              <div
                className={`inset-0 bg-light-leak pointer-events-none z-0
              transition duration-2000 ease-out-expo
                 ${inView || isMobile ? 'opacity-100 delay-700' : 'opacity-0 delay-300'}
              `}
              ></div>
              <div className="hidden md:block inset-0 green-fade-out pointer-events-none z-20 opacity-[5%] group-hover/card:opacity-20 transition duration-1000 ease-out-expo mix-blend-screen"></div>
              <div
                className={`inset-0 pointer-events-none z-20 transition ease-out-expo duration-1500 mix-blend-screen blur-2xl opacity-50 origin-top-center
                ${inView || isMobile ? 'scale-150 delay-100' : 'scale-0 delay-100'}
              `}
              >
                <div className="inset-0 h-1/2 bg-gradient animate-card-spot-light"></div>
              </div>
              <div className="px-6 py-10 md:px-12 z-10">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Card

export const GPUCard = ({
  children,
  className = '',
  minHeight = '',
}: {
  children: ReactNode
  className?: string
  minHeight?: string
}) => {
  const ref = useRef(null)
  const borderRef = useRef(null)
  const inView = useInView(ref, {
    once: false,
    amount: 0,
  })
  const isMobile = useMediaQuery('(max-width: 1024px)')
    const [gradientAnimationClass, setGradientAnimationClass] = useState(
    'before:[animation-play-state:running]',
  )
  const [glowAnimationClass, setGlowAnimationClass] = useState(
    'md:after:[animation-play-state:running] md:after:opacity-40',
  )
  const { contextSafe } = useGSAP({ scope: ref })

  const onHover = contextSafe(() => {
    gsap.to(borderRef.current, {
      rotation: '+=720deg',
      duration: 8,
      ease: 'expo.out',
    })
  })

  const handleAnimationClass = contextSafe(() => {
    const value = { current: 0 }
    gsap.timeline().to(value, {
      current: 1,
      duration: 5,
      ease: 'expo.out',
      onComplete: () => {
        setGradientAnimationClass('before:[animation-play-state:paused]')
        setGlowAnimationClass('md:after:[animation-play-state:paused] md:after:opacity-0')
      },
    })
  })

  useEffect(() => {
    if (inView) {
      handleAnimationClass()
      return
    }
    setGradientAnimationClass('before:[animation-play-state:running]')
    setGlowAnimationClass('md:after:[animation-play-state:running] md:after:opacity-40')
  }, [handleAnimationClass, inView])

  const onHoverOut = contextSafe(() => {
    gsap.killTweensOf(borderRef.current)
  })

  return (
    <div className="hit-box group/card relative perspective-1600 preserve-3d h-full w-full flex-shrink-0 flex-grow-0 z-10">
      <div
        ref={ref}
        className={`relative h-full
        ${inView || isMobile ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-28'}
            transition duration-1000 ease-expo-out transform
            group-hover/card:-rotate-x-6 group-hover/card:scale-95
        `}
        style={{ minHeight }}
        onMouseEnter={onHover}
        onMouseLeave={onHoverOut}
      >
        <div
          className={`flex flex-col col-span-1 items-start justify-start p-px md:p-[2px] rounded-card-bg md:rounded-card-bg-lg w-full relative group/card ${className} z-0

          before:absolute before:content-[''] before:inset-0 before:z-0 before:pointer-events-none
          before:card-border-bg before:rounded-card-bg before:md:rounded-card-bg-lg before:opacity-40
          before:group-hover/card:opacity-40 before:transition before:duration-1000 before:ease-linear
          ${gradientAnimationClass} before:group-hover/card:[animation-play-state:running]
          
          md:after:absolute md:after:content-[''] md:after:inset-0 md:after:z-0 md:after:pointer-events-none
          md:after:card-border-bg md:after:rounded-card-bg md:after:md:rounded-card-bg-lg md:after:blur-3xl
          ${glowAnimationClass} md:after:group-hover/card:opacity-20 md:after:transition md:after:duration-1000 md:after:ease-linear
        `}
        >
          <div className="grid grid-pile h-full w-full rounded-card md:rounded-card-lg overflow-hidden relative bg-black z-10">
            <div
              className={`grid grid-pile grid-cols-1 relative w-full h-full transition ease-out-expo duration-1500 z-10 pointer-events-auto
                ${inView || isMobile ? 'opacity-100  translate-y-0 delay-500' : 'opacity-0 translate-y-4 delay-300'}
              `}
            >
              <div
                className={`inset-0 bg-light-leak pointer-events-none z-0
                  transition duration-2000 ease-out-expo
                  ${inView || isMobile ? 'opacity-100 delay-700' : 'opacity-0 delay-300'}
                `}
              ></div>
              <div className="hidden md:block inset-0 green-fade-out pointer-events-none z-20 opacity-[5%] group-hover/card:opacity-20 transition duration-1000 ease-out-expo mix-blend-screen"></div>
              <div
                className={`inset-0 pointer-events-none z-20 transition ease-out-expo duration-1500 mix-blend-screen blur-2xl opacity-50 origin-top-center
                ${inView || isMobile ? 'scale-150 delay-100' : 'scale-0 delay-100'}
              `}
              >
                <div className="inset-0 h-1/2 bg-gradient animate-card-spot-light"></div>
              </div>
              <div className="px-6 py-10 md:px-12 z-10">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
